import { MontoCRC, MontoUSD } from '@/components/dominio';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useCuentasBancoBC } from '@/hooks/useCuentasBancoBC';
import {
  useArchivoBancoPropuesta,
  useArchivosBancoGuardados,
  useUltimosEnviosPorCuenta,
  useValidacionArchivoBanco,
} from '@/hooks/usePropuestasPago';
import { esCuentaBAC, esCuentaBNCR } from '@adelante/shared';
import type { ArchivoBancoResultado, MotivoCuentaIncompleta } from '@adelante/shared';
import { ArrowClockwise, ArrowSquareOut, DownloadSimple, Warning } from '@phosphor-icons/react';
import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';

interface Props {
  idPropuesta: number;
  /** Cuentas bancarias de origen ya usadas en la propuesta (para elegir de cuál generar el archivo). */
  cuentasUsadas: string[];
  open: boolean;
  onClose: () => void;
}

/**
 * `toISOString()` convierte a UTC — en Costa Rica (UTC-6) eso puede adelantar la fecha un día
 * entero (ej. generar el archivo a las 8pm hora local ya cae en el día siguiente en UTC), y el
 * archivo terminaría con la fecha de mañana en vez de la de hoy. Se arma con los componentes de
 * fecha LOCALES en su lugar.
 */
const TEXTO_MOTIVO: Record<MotivoCuentaIncompleta, string> = {
  'sin-proveedor': 'No está en Proveedores',
  'sin-iban': 'Sin IBAN en colones',
  'sin-iban-dolares': 'Sin IBAN en dólares',
  'iban-formato': 'IBAN con formato inválido (debe ser CR + 20 dígitos)',
  'iban-digito-control': 'IBAN con dígito de control inválido (¿número mal digitado?)',
};

function hoyISO(): string {
  const ahora = new Date();
  const anio = ahora.getFullYear();
  const mes = String(ahora.getMonth() + 1).padStart(2, '0');
  const dia = String(ahora.getDate()).padStart(2, '0');
  return `${anio}-${mes}-${dia}`;
}

export function GenerarArchivoBancoDialog({ idPropuesta, cuentasUsadas, open, onClose }: Props) {
  const { data: cuentasBancoData } = useCuentasBancoBC();
  const cuentasBanco = cuentasBancoData?.data ?? [];
  const generar = useArchivoBancoPropuesta();
  // Se carga desde que el componente existe (no solo cuando `open` es true) — el diálogo está
  // siempre montado en la página (solo se oculta/muestra), así que si esta consulta esperara a
  // `open`, la primera vez que se abre en la sesión el resultado guardado llega un instante
  // DESPUÉS de que el modal ya arrancó su animación de apertura, y el contenido crece de golpe a
  // mitad de esa animación — eso es lo que generaba el artefacto visual (recuadro raro). Al
  // cargarla de fondo apenas se monta la página, ya está en caché para cuando el usuario abre el
  // diálogo, así el contenido correcto aparece en el primer render, sin salto.
  const { data: guardadosData } = useArchivosBancoGuardados(idPropuesta);
  const guardados = useMemo(() => guardadosData?.data ?? [], [guardadosData]);
  const { data: ultimosData } = useUltimosEnviosPorCuenta();
  const ultimosPorCuenta = useMemo(() => ultimosData?.data ?? [], [ultimosData]);

  // El .prn es el formato de Sucursal Electrónica de BAC — solo las cuentas BAC generan archivo
  // (uno por cuenta). Mientras las cuentas de BC no cargan, no se puede saber el banco: se dejan
  // pasar para no vaciar el selector un instante.
  const { cuentasBAC, cuentasOtroBanco } = useMemo(() => {
    const esBAC = (numero: string) => {
      const cuenta = cuentasBanco.find((c) => c.number === numero);
      return cuenta ? esCuentaBAC(cuenta.displayName) : true;
    };
    return {
      cuentasBAC: cuentasUsadas.filter(esBAC),
      // Las cuentas BN ya tienen su propio archivo (.SIN): no se avisan como "sin archivo".
      cuentasOtroBanco: cuentasUsadas.filter((n) => {
        if (esBAC(n)) return false;
        const cuenta = cuentasBanco.find((c) => c.number === n);
        return !(cuenta && esCuentaBNCR(cuenta.displayName));
      }),
    };
  }, [cuentasUsadas, cuentasBanco]);

  const [cuentaBancoOrigen, setCuentaBancoOrigen] = useState(cuentasBAC[0] ?? '');
  // El monto se muestra en la moneda de la cuenta de origen (dólares para las cuentas en USD).
  const Monto =
    cuentasBanco.find((c) => c.number === cuentaBancoOrigen)?.currencyCode === 'USD'
      ? MontoUSD
      : MontoCRC;
  const [planCode, setPlanCode] = useState('');
  const [envioNo, setEnvioNo] = useState('');
  const [fecha, setFecha] = useState(hoyISO());
  const [resultado, setResultado] = useState<ArchivoBancoResultado | null>(null);
  const [metaGuardado, setMetaGuardado] = useState<{ generadoEn: string } | null>(null);
  const [regenerando, setRegenerando] = useState(false);

  // Las líneas de la propuesta (y por lo tanto `cuentasUsadas`) suelen cargar después de que
  // este diálogo ya está montado — sincronizar la selección cuando la lista cambia o se abre.
  // biome-ignore lint/correctness/useExhaustiveDependencies: no depender de cuentaBancoOrigen, si no se resetea en cada tecleo
  useEffect(() => {
    if (open && !cuentasBAC.includes(cuentaBancoOrigen)) {
      setCuentaBancoOrigen(cuentasBAC[0] ?? '');
    }
  }, [open, cuentasBAC]);

  const ultimoEnvioCuenta = ultimosPorCuenta.find((u) => u.cuentaBancoOrigen === cuentaBancoOrigen);

  // Si ya existe un archivo generado para la cuenta elegida, mostrarlo directo (con sus datos)
  // en vez de obligar a pasar por "Generar" de nuevo. Si no, cada cuenta BAC tiene su propio
  // código de plan (BOK): se pre-llena con el último que se usó para esa cuenta, y el envío se
  // deja vacío (con el último como referencia) para que se escriba a conciencia.
  useEffect(() => {
    if (!open || regenerando) return;
    const guardado = guardados.find((g) => g.cuentaBancoOrigen === cuentaBancoOrigen);
    if (guardado) {
      setPlanCode(guardado.planCode);
      setEnvioNo(String(guardado.envioNo));
      setFecha(guardado.fecha);
      setResultado(guardado);
      setMetaGuardado({ generadoEn: guardado.generadoEn });
    } else {
      setPlanCode(ultimoEnvioCuenta?.planCode ?? '');
      setEnvioNo('');
      setResultado(null);
      setMetaGuardado(null);
    }
  }, [open, regenerando, guardados, cuentaBancoOrigen, ultimoEnvioCuenta]);

  // Revisión previa: qué proveedores de esta cuenta tienen la cuenta incompleta, para corregirlos
  // en Proveedores antes de generar (y no enterarse después por la lista de excluidos).
  const { data: validacionData, isFetching: validando } = useValidacionArchivoBanco(
    idPropuesta,
    cuentaBancoOrigen,
    open && !resultado,
  );
  const validacion = validacionData?.data;
  const incompletos = validacion?.incompletos ?? [];
  const cantidadExcluidos = incompletos.filter((p) => p.excluido).length;

  const envioNoNumero = Number(envioNo);
  const envioValido = Number.isInteger(envioNoNumero) && envioNoNumero > 0 && envioNoNumero <= 999;

  function generarArchivo() {
    if (!cuentaBancoOrigen || !planCode.trim() || !envioNo) return;
    setResultado(null);
    generar.mutate(
      {
        id: idPropuesta,
        cuentaBancoOrigen,
        planCode: planCode.trim(),
        envioNo: Number(envioNo),
        fecha,
      },
      {
        onSuccess: (r) => {
          if (r.data) {
            setResultado(r.data);
            setMetaGuardado(null);
            setRegenerando(false);
          } else toast.error(r.error?.mensaje ?? 'Error al generar el archivo');
        },
        onError: (e) => toast.error(e instanceof Error ? e.message : 'Error al generar el archivo'),
      },
    );
  }

  /**
   * El .prn de BAC usa Latin-1/Windows-1252 (tildes, Ñ en un solo byte) — si se arma el Blob
   * directo desde el string de JS, el navegador lo codifica como UTF-8 y esos caracteres salen
   * mal en el archivo. Se convierte cada char code a su byte Latin-1 antes de descargar.
   */
  function aBytesLatin1(texto: string): Uint8Array {
    const bytes = new Uint8Array(texto.length);
    for (let i = 0; i < texto.length; i++) bytes[i] = texto.charCodeAt(i) & 0xff;
    return bytes;
  }

  function descargarArchivo(contenido: string, nombreArchivo: string, comoLatin1: boolean) {
    const partes: BlobPart[] = comoLatin1 ? [aBytesLatin1(contenido) as BlobPart] : [contenido];
    const blob = new Blob(partes, { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = nombreArchivo;
    a.click();
    URL.revokeObjectURL(url);
  }

  function descargarPrn() {
    if (!resultado) return;
    descargarArchivo(resultado.contenidoPrn, resultado.nombreArchivoPrnSugerido, true);
  }

  function descargarCsv() {
    if (!resultado) return;
    descargarArchivo(resultado.contenidoCsv, resultado.nombreArchivoSugerido, false);
  }

  function cerrar() {
    setResultado(null);
    setMetaGuardado(null);
    setRegenerando(false);
    onClose();
  }

  function volverAGenerar() {
    setRegenerando(true);
    setResultado(null);
    setMetaGuardado(null);
  }

  return (
    <Dialog open={open} onOpenChange={(o) => !o && cerrar()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Generar archivo de banco</DialogTitle>
          <DialogDescription>
            Arma el archivo .prn que se sube en Sucursal Electrónica — Planillas y Proveedores. El
            CSV es solo un respaldo/histórico interno, el banco no lo acepta.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="space-y-1">
            <Label htmlFor="cuenta-banco-origen">Cuenta bancaria de origen</Label>
            {/*
              <select> nativo del navegador, no el componente Select (Radix): ese usa su propio
              popover portado a <body> con overlay independiente — dentro de un Dialog eso venía
              causando un problema visual de superposición difícil de diagnosticar (contenido de
              la página de atrás filtrándose). El <select> nativo no tiene esa capa flotante
              propia, así que no puede tener ese problema.
            */}
            <select
              id="cuenta-banco-origen"
              value={cuentaBancoOrigen}
              onChange={(e) => setCuentaBancoOrigen(e.target.value)}
              className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
            >
              {cuentasBAC.length === 0 && <option value="">Sin cuentas BAC en la propuesta</option>}
              {cuentasBAC.map((numero) => {
                const cuenta = cuentasBanco.find((c) => c.number === numero);
                return (
                  <option key={numero} value={numero}>
                    {cuenta ? `${cuenta.displayName} (${cuenta.number})` : numero}
                  </option>
                );
              })}
            </select>
            {cuentasOtroBanco.length > 0 && (
              <p className="flex items-start gap-2 rounded-md border border-amber-400/60 bg-amber-50 p-2 text-xs">
                <Warning
                  className="mt-0.5 h-4 w-4 shrink-0 text-amber-600"
                  weight="bold"
                  aria-hidden="true"
                />
                <span>
                  Los pagos de{' '}
                  <strong>
                    {cuentasOtroBanco
                      .map((n) => cuentasBanco.find((c) => c.number === n)?.displayName ?? n)
                      .join(', ')}
                  </strong>{' '}
                  <strong>no van en ningún archivo</strong>: el .prn es solo de cuentas BAC. Si ese
                  pago debía salir por BAC, cambiá la cuenta de origen en la propuesta (cada cuenta
                  BAC genera su propio archivo, con su propio código BOK).
                </span>
              </p>
            )}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label htmlFor="plan-code">Código Plan</Label>
              <Input
                id="plan-code"
                value={planCode}
                onChange={(e) => setPlanCode(e.target.value)}
                placeholder="Ej. BOK3"
                autoComplete="off"
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="envio-no">Número de envío</Label>
              <Input
                id="envio-no"
                type="number"
                value={envioNo}
                onChange={(e) => setEnvioNo(e.target.value)}
                placeholder="Ej. 316"
                min={1}
                max={999}
                autoComplete="off"
              />
              {ultimoEnvioCuenta && !metaGuardado && (
                <p className="text-xs text-muted-foreground">
                  Último envío de esta cuenta: {ultimoEnvioCuenta.envioNo} (
                  {ultimoEnvioCuenta.planCode})
                </p>
              )}
            </div>
          </div>
          <div className="space-y-1">
            <Label htmlFor="fecha-envio">Fecha</Label>
            <Input
              id="fecha-envio"
              type="date"
              value={fecha}
              onChange={(e) => setFecha(e.target.value)}
            />
          </div>
          {envioValido && !resultado && (
            <p className="text-xs text-muted-foreground">
              Consecutivos: {envioNoNumero * 100 + 1}, {envioNoNumero * 100 + 2}, … (envío + 01, 02,
              …)
            </p>
          )}
        </div>

        {!resultado && validacion && (
          <div
            className={`space-y-2 rounded-md border p-3 text-sm ${
              incompletos.length > 0
                ? 'border-destructive/40 bg-destructive/5'
                : 'border-adelante-primary/30 bg-adelante-primary/5'
            }`}
          >
            {incompletos.length === 0 ? (
              <p className="text-xs text-muted-foreground">
                {validando
                  ? 'Revisando cuentas…'
                  : `Los ${validacion.totalProveedores} proveedores de esta cuenta tienen IBAN completo.`}
              </p>
            ) : (
              <>
                <div className="flex items-start gap-2">
                  <Warning
                    className="mt-0.5 h-4 w-4 shrink-0 text-destructive"
                    weight="bold"
                    aria-hidden="true"
                  />
                  <p>
                    {incompletos.length} de {validacion.totalProveedores} proveedores con la cuenta
                    incompleta
                    {cantidadExcluidos > 0 && ` — ${cantidadExcluidos} quedarían fuera del archivo`}
                    . Corregilos en Proveedores antes de generar.
                  </p>
                </div>
                <ul className="max-h-48 space-y-1 overflow-y-auto text-xs">
                  {incompletos.map((p) => (
                    <li
                      key={p.vendorCode}
                      className="flex items-baseline justify-between gap-3 border-b border-border/50 pb-1 last:border-0"
                    >
                      <span className="min-w-0">
                        <span className="font-medium">{p.vendorName ?? p.vendorCode}</span>{' '}
                        <span className="font-mono text-muted-foreground">{p.vendorCode}</span>
                        <br />
                        <span className={p.excluido ? 'text-destructive' : 'text-amber-700'}>
                          {TEXTO_MOTIVO[p.motivo]}
                          {p.excluido ? ' — queda fuera' : ' — se incluye igual'}
                        </span>
                      </span>
                      <span className="shrink-0 font-mono">
                        <Monto monto={p.monto} />
                      </span>
                    </li>
                  ))}
                </ul>
                <div className="flex items-center justify-between gap-2">
                  <a
                    href="/flujo-semanal/proveedores"
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-1 text-xs font-medium text-adelante-primary-dark underline"
                  >
                    Abrir Proveedores en otra pestaña
                    <ArrowSquareOut className="h-3 w-3" weight="bold" aria-hidden="true" />
                  </a>
                  <span className="text-xs text-muted-foreground">
                    {validando ? 'Revisando…' : 'Se actualiza al volver a esta pestaña'}
                  </span>
                </div>
              </>
            )}
          </div>
        )}

        {resultado && (
          <div className="space-y-3 rounded-md border border-adelante-primary/30 bg-adelante-primary/5 p-3 text-sm">
            {metaGuardado && (
              <p className="text-xs text-muted-foreground">
                Ya generado el{' '}
                {new Date(metaGuardado.generadoEn).toLocaleString('es-CR', {
                  dateStyle: 'short',
                  timeStyle: 'short',
                })}{' '}
                — descargalo de nuevo abajo, o generá uno nuevo si cambió algo.
              </p>
            )}
            <div className="grid grid-cols-3 gap-3">
              <div>
                <p className="text-xs text-muted-foreground">Monto</p>
                <p className="font-mono text-base font-semibold text-adelante-primary-dark">
                  <Monto monto={resultado.montoTotal} />
                </p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Cantidad de líneas</p>
                <p className="font-mono text-base font-semibold text-adelante-primary-dark">
                  {resultado.totalIncluidas}
                </p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Consecutivo asignado</p>
                <p className="font-mono text-base font-semibold text-adelante-primary-dark">
                  {resultado.consecutivoInicial === resultado.consecutivoFinal
                    ? resultado.consecutivoInicial
                    : `${resultado.consecutivoInicial}–${resultado.consecutivoFinal}`}
                </p>
              </div>
            </div>
            <p className="text-xs text-muted-foreground">
              Monto y cantidad de líneas son los que BAC pide al subir el archivo en Sucursal
              Electrónica — para cotejar sin tener que abrir el CSV.
            </p>
            {resultado.totalExcluidas > 0 && (
              <div className="space-y-1 border-t border-adelante-primary/20 pt-2">
                <p className="text-destructive">
                  {resultado.totalExcluidas} proveedor(es) excluidos — sin IBAN en Proveedores:
                </p>
                <ul className="list-inside list-disc text-xs text-muted-foreground">
                  {resultado.excluidas.map((e) => (
                    <li key={e.vendorCode}>
                      {e.vendorName ?? e.vendorCode} ({e.vendorCode})
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}

        {/*
          `flex-wrap` + `min-w-0`: sin esto, los botones (que llevan whitespace-nowrap) suman un
          ancho mínimo mayor al del modal y, como DialogContent es un grid, estiran la columna
          interna más allá del ancho de la caja blanca (max-w-lg) — eso hacía que TODO el
          contenido (incluido el texto de la descripción de arriba) se desbordara por la derecha,
          quedando texto pintado fuera del fondo blanco.
        */}
        <div className="flex min-w-0 flex-wrap justify-end gap-2">
          <Button variant="outline" onClick={cerrar}>
            Cerrar
          </Button>
          {resultado ? (
            <>
              {metaGuardado && (
                <Button variant="outline" onClick={volverAGenerar}>
                  <ArrowClockwise className="h-4 w-4" weight="bold" aria-hidden="true" />
                  Generar de nuevo
                </Button>
              )}
              <Button variant="outline" onClick={descargarCsv}>
                <DownloadSimple className="h-4 w-4" weight="bold" aria-hidden="true" />
                Descargar CSV
              </Button>
              <Button onClick={descargarPrn}>
                <DownloadSimple className="h-4 w-4" weight="bold" aria-hidden="true" />
                Descargar .prn
              </Button>
            </>
          ) : (
            <Button
              onClick={generarArchivo}
              disabled={!cuentaBancoOrigen || !planCode.trim() || !envioValido || generar.isPending}
            >
              {generar.isPending
                ? 'Generando...'
                : cantidadExcluidos > 0
                  ? `Generar sin ${cantidadExcluidos} proveedor(es)`
                  : 'Generar'}
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
